import { Turnkey } from '@turnkey/sdk-browser';
import { tracked } from '@glimmer/tracking';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { uncompressRawPublicKey } from '@turnkey/crypto';
import { Buffer } from 'buffer';

export default class TurnkeyService extends Service {
  @service account;
  @tracked session = null;
  localStorageTokenName = 'turnkey-token';
  publicKey = '';

  get isAuthenticated() {
    return (
      this.session !== null &&
      this.session.activity.intent.createReadWriteSessionIntentV2.expirationSeconds > Math.floor(Date.now() / 1000)
    );
  }

  // Configure the Turnkey SDK and initialize the indexedDB client in order to get the public key
  async initializeTurnkey() {
    const config = {
      apiBaseUrl: 'https://api.turnkey.com',
      defaultOrganizationId: this.account.walletData.organizationId,
    };

    const turnkeySDK = new Turnkey(config);
    this.indexedDbClient = await turnkeySDK.indexedDbClient();
    await this.indexedDbClient.init();
    this.publicKey = await this.indexedDbClient.getPublicKey();
    if (this.session === null) {
      const jwt = window.localStorage.getItem(this.localStorageTokenName);
      if (jwt) {
        const { expiry } = this.decodeSessionJwt(jwt);
        if (expiry < Math.floor(Date.now() / 1000)) {
          window.localStorage.removeItem(this.localStorageTokenName);
          this.session = null;
        } else {
          this.createSession(jwt);
        }
      }
    }
  }

  async createSession(jwt) {
    const { userId, organizationId, expiry, publicKey } = this.decodeSessionJwt(jwt);
    try {
      this.session = await this.indexedDbClient.createReadWriteSession({
        organizationId: organizationId,
        timestampMs: String(Date.now()),
        targetPublicKey: this.toUncompressedHex(publicKey),
        userId: userId,
        expirationSeconds: String(expiry),
        invalidateExisting: true,
      });
    } catch (error) {
      window.localStorage.removeItem(this.localStorageTokenName);
      this.session = null;
      throw error;
    }
  }

  decodeSessionJwt(token) {
    const [, payload] = token.split('.');
    if (!payload) {
      throw new Error('Invalid JWT: Missing payload');
    }

    const decoded = JSON.parse(atob(payload));
    const {
      exp,
      public_key: publicKey,
      session_type: sessionType,
      user_id: userId,
      organization_id: organizationId,
    } = decoded;

    if (!exp || !publicKey || !sessionType || !userId || !organizationId) {
      throw new Error('JWT payload missing required fields');
    }

    return {
      sessionType,
      userId,
      organizationId,
      expiry: exp,
      publicKey,
    };
  }

  toUncompressedHex(compressedKey) {
    const compressedBytes = new Uint8Array(Buffer.from(compressedKey, 'hex'));
    // Uncompress to get 65-byte uncompressed key
    const uncompressedKey = uncompressRawPublicKey(compressedBytes);
    const uncompressedHex = Buffer.from(uncompressedKey).toString('hex');
    return uncompressedHex;
  }

  async signTransaction(payload) {
    try {
      // Ensure payload is a hex-encoded string for PAYLOAD_ENCODING_HEXADECIMAL      
      const jsonString = JSON.stringify(payload);
      const payloadHex = Buffer.from(jsonString, 'utf8').toString('hex');
      const res = await this.indexedDbClient.signRawPayload({
        organizationId: this.account.walletData.organizationId,
        timestampMs: String(Date.now()),
        signWith: this.account.walletData.walletAddress,
        payload: payloadHex,
        hashFunction: 'HASH_FUNCTION_KECCAK256',
        encoding: 'PAYLOAD_ENCODING_HEXADECIMAL',
      });
      return res.activity.result.signRawPayloadResult;
    } catch (error) {
      window.localStorage.removeItem(this.localStorageTokenName);
      this.client = null;
      throw error;
    }
  }
}
